import { HttpError } from '@microsoft/signalr';
import { ScopeError } from 'src/components/Consent/State/consentClientState';
import { HubService, type IHubService } from 'src/services/HubService';
import { type Scope } from 'src/state/server';
import { LoggerServiceFactory } from './LoggerService';
export type IConsentServiceListener = {
  onError: (callback: (type: ScopeError) => void) => void;
  onScope: (callback: (scope: Scope) => void) => void;
  onScopeUpdated: (callback: () => void) => void;
};

export interface IConsentService extends IHubService, IConsentServiceListener {
  updateScope(scope: Record<string, string[]>): Promise<void>;
}

export enum ConsentServiceEvents {
  ReceiveScopeAsync = 'ReceiveScopeAsync',
  OnConsentError = 'ConsentError',
  OnScopeUpdated = 'ScopeUpdated',
}

export class ConsentService extends HubService implements IConsentService {
  constructor(scopeName: string) {
    super({
      path: `/hubs/consent?scope=${scopeName}`,
      logger: LoggerServiceFactory.create('ConsentService'),
    });
  }

  public onScope(callback: (scope: Scope) => void): string {
    return this.on(ConsentServiceEvents.ReceiveScopeAsync, (sc: Scope) => {
      this.logger.trace('Received scope from Consent hub', {
        data: {
          fullName: sc.fullName,
          status: sc.status,
          expires: sc.expires,
          requiresSca: sc.requiresSca,
          requiresConsent: sc.requiresConsent,
          userName: sc.userName,
          hasData: !!sc.data,
          hasAccounts: !!sc.accounts,
        },
      });
      callback(sc);
    });
  }

  public onError(callback: (type: ScopeError) => void): string {
    return this.on(ConsentServiceEvents.OnConsentError, callback);
  }

  public onScopeUpdated(callback: () => void): string {
    return this.on(ConsentServiceEvents.OnScopeUpdated, callback);
  }

  async start() {
    return super.start().catch((e: HttpError) => {
      if (e.message.includes('403')) {
        this.broadcast(
          ConsentServiceEvents.OnConsentError,
          ScopeError.AccessDenied,
        );
      } else {
        this.broadcast(ConsentServiceEvents.OnConsentError, ScopeError.Unknown);
      }
    });
  }

  async updateScope(scope: Record<string, string[]>) {
    await this.send('UpdateScopeAsync', scope);
    this.broadcast(ConsentServiceEvents.OnScopeUpdated);
  }
}

export const ConsentServiceToken = Symbol(ConsentService.name);
